'use client';
import { __rest } from '../../node_modules/tslib/tslib.es6.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import styled, { css } from 'styled-components';
import { withTitan } from '../../hocs/withTitan/withTitan.js';
import { useBinding } from '../../hooks/useBinding/useBinding.js';
import { keyboardFocusShadowAfter } from '../../mixins/keyboardFocus.js';
import { resetList } from '../../mixins/resetList.js';
import { spaceProp } from '../../mixins/spaceProp.js';
import { Clickable } from '../Clickable/Clickable.js';
import { Text } from '../Text/Text.js';
import { StepLabel } from './StepLabel.js';

const StyledStepper = styled.ul.withConfig({
  componentId: "sc-1jtsuhm-0"
})(["position:relative;", " ", ""], resetList, ({
  spaceAfter
}) => spaceProp(spaceAfter));
const StyledStep = styled.li.withConfig({
  componentId: "sc-1jtsuhm-1"
})(["margin-bottom:", ";"], ({
  theme
}) => theme.components.stepper.stepMarginBottom);
const StyledSubStep = styled(Text).withConfig({
  componentId: "sc-1jtsuhm-2"
})(["cursor:pointer;margin-bottom:", ";", " ", " ", ""], ({
  theme
}) => theme.components.stepper.subStepMarginBottom, ({
  isDisabled,
  theme
}) => isDisabled && css(["color:", ";"], theme.components.stepper.colorDisabled), ({
  isActive
}) => isActive && css(["font-weight:600;"]), ({
  isDisabled
}) => !isDisabled && css(["&:hover,&:focus{text-decoration:underline;}"]));
const StyledSubStepButton = styled(Clickable).withConfig({
  componentId: "sc-1jtsuhm-3"
})(["text-align:left;", ""], keyboardFocusShadowAfter({
  offset: -2
}));
const StyledSubSteps = styled.ul.withConfig({
  componentId: "sc-1jtsuhm-4"
})(["", " display:flex;flex-direction:column;margin-left:29px;margin-top:", ";margin-bottom:", ";"], resetList, ({
  theme
}) => theme.components.stepper.subStepsMarginTop, ({
  theme
}) => theme.components.stepper.subStepsMarginBottom);
const StyledLine = styled.div.withConfig({
  componentId: "sc-1jtsuhm-5"
})(["min-height:100%;width:2px;margin-left:11px;background-color:", ";border-radius:100px;height:", ";"], ({
  theme,
  isActive,
  isCompleted
}) => isCompleted && !isActive ? theme.components.stepper.colorCompleted : theme.components.stepper.lineColor, ({
  theme,
  isCollapsed
}) => isCollapsed ? theme.components.stepper.lineHeightCollapsed : 'initial');
const StyledStepInner = styled.div.withConfig({
  componentId: "sc-1jtsuhm-6"
})(["display:flex;"]);
const Line = ({
  isActive = false,
  isCollapsed = false,
  isCompleted = false
}) => {
  return jsx(StyledLine, {
    isActive: isActive,
    isCollapsed: isCollapsed,
    isCompleted: isCompleted
  });
};
const Step = _a => {
  var {
      id,
      subSteps,
      isLastStep,
      activeStepId,
      isCompleted,
      isActive,
      onClick
    } = _a,
    rest = __rest(_a, ["id", "subSteps", "isLastStep", "activeStepId", "isCompleted", "isActive", "onClick"]);
  return jsxs(StyledStep, {
    children: [jsx(StepLabel, Object.assign({
      "aria-current": isActive,
      as: "button",
      isActive: isActive,
      isCompleted: isCompleted,
      spaceAfter: 2,
      onClick: () => onClick === null || onClick === void 0 ? void 0 : onClick(id)
    }, rest)), jsx(StyledStepInner, {
      children: subSteps && isActive ? jsxs(Fragment, {
        children: [jsx(Line, {
          isActive: true,
          isCompleted: isCompleted
        }), jsx(StyledSubSteps, {
          children: subSteps.map(subStep => jsx(StyledSubStep, {
            "aria-current": activeStepId === subStep.id,
            as: "li",
            color: "grey80",
            isActive: activeStepId === subStep.id,
            isDisabled: subStep.isDisabled,
            type: "label",
            children: jsx(StyledSubStepButton, {
              disabled: subStep.isDisabled,
              type: "button",
              onClick: onClick ? () => onClick(subStep.id) : undefined,
              children: subStep.label
            })
          }, subStep.label))
        })]
      }) : isLastStep && jsx(Line, {
        isCollapsed: true,
        isActive: activeStepId === id,
        isCompleted: isCompleted
      })
    })]
  });
};
const isActiveTrail = (activeStepId, step) => {
  if (activeStepId === step.id) {
    return true;
  }
  if (!step.subSteps) {
    return false;
  }
  return Boolean(step.subSteps.find(step => step.id === activeStepId));
};
const Stepper = /*#__PURE__*/withTitan(({
  label = 'Voortgang',
  steps,
  activeStep,
  defaultActiveStep,
  onChange,
  testId = 'stepper',
  spaceAfter
}) => {
  const [activeStepId, setActiveStepId] = useBinding(defaultActiveStep, activeStep, onChange, '');
  const handleClick = id => {
    const index = steps.findIndex(x => x.id === id);
    const step = steps[index];
    if (step === null || step === void 0 ? void 0 : step.isDisabled) return;
    setActiveStepId((step === null || step === void 0 ? void 0 : step.subSteps) ? step.subSteps[0].id : id);
  };
  return jsx("nav", {
    "aria-label": label,
    children: jsx(StyledStepper, {
      "data-test-id": testId,
      spaceAfter: spaceAfter,
      children: steps.map((step, index) => jsx(Step, Object.assign({
        activeStepId: activeStepId,
        isActive: isActiveTrail(activeStepId, step),
        isLastStep: !!steps[index + 1],
        onClick: handleClick
      }, step), step.step))
    })
  });
}, {
  name: 'Stepper'
});

export { Stepper };
//# sourceMappingURL=Stepper.js.map
